/**
 * /dashboard/expenses/$id — Expense detail (Phase 3d).
 *
 * IMPORTANT — there is NO GET /expenses/{id} endpoint and no
 * expense-timeline table (deliberate; same pattern as leave 4.3). The
 * single expense is DERIVED from the role-scoped list query
 * (useExpenseFromList) — the same list the index page uses, so the row
 * reflects whatever the caller's SQL scope allows and stays consistent
 * after a submit/decision/delete invalidation.
 *
 * The "timeline" here is NOT a synthetic event feed — it is the
 * expense's own lifecycle fields (created_at → status, decided_at,
 * decision_notes, reimbursed_batch_id) presented as an honest status &
 * decision summary.
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/expenses/[id]/page.tsx.
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, CalendarDays, FileText, Store } from "lucide-react";
import {
  useExpenseCategories,
  useExpenseFromList,
} from "@/hooks/use-expenses";
import { useDirectory } from "@/hooks/use-directory";
import { toApiError } from "@/lib/api";
import {
  fmtDate,
  fmtDateTime,
  formatPaise,
} from "@/lib/expense-format";
import { useAuth } from "@/lib/auth";
import { useExpenseRoles } from "@/components/expenses/use-expense-roles";
import { ExpensesModuleShell } from "@/components/shared/expenses-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { ExpenseFeatureGate } from "@/components/expenses/expense-feature-gate";
import { ExpenseActions } from "@/components/expenses/expense-actions";
import { ReceiptManager } from "@/components/expenses/receipt-manager";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/expenses/$id",
)({
  component: ExpenseDetailRoute,
});

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-input px-3 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-gray-900">
        {value}
      </dd>
    </div>
  );
}

function ExpenseDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const { user } = useAuth();
  const { isExpenseAdmin } = useExpenseRoles();
  const { expense, isLoading, isError, error } =
    useExpenseFromList(numId);
  const catsQ = useExpenseCategories();
  const dir = useDirectory({ includeInactive: true });

  const category = catsQ.data?.categories.find(
    (c) => c.id === expense?.category_id,
  );
  const categoryLabel = expense
    ? (category?.name ?? `Category #${expense.category_id}`)
    : "";

  const isOwner = !!expense && user?.user_id === expense.user_id;
  const canModify = isOwner || isExpenseAdmin;

  return (
    <ExpensesModuleShell
      title="Expense"
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/expenses" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <ExpenseFeatureGate>
        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load this expense</AlertTitle>
            <AlertDescription>
              {toApiError(error).message}
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && !expense && (
          <Alert>
            <AlertTitle>Expense not found</AlertTitle>
            <AlertDescription>
              This expense doesn’t exist or isn’t visible to your role.{" "}
              <Link
                to="/app/$tenantSlug/expenses" params={{ tenantSlug }}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Back to expenses
              </Link>
              .
            </AlertDescription>
          </Alert>
        )}

        {expense && (
          <div className="space-y-5">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">
                      {categoryLabel} ·{" "}
                      {formatPaise(expense.amount_paise)}
                    </CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {isOwner
                        ? "Your claim"
                        : dir.label(expense.user_id, expense.user_name)}
                    </p>
                  </div>
                  <StatusBadge status={expense.status} />
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Row
                    label="Amount"
                    value={formatPaise(expense.amount_paise)}
                  />
                  <Row
                    label="Expense date"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                        {fmtDate(expense.expense_date)}
                      </span>
                    }
                  />
                  <Row label="Category" value={categoryLabel} />
                  <Row
                    label="Merchant"
                    value={
                      expense.merchant ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Store className="h-3.5 w-3.5 text-muted-foreground" />
                          {expense.merchant}
                        </span>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <Row
                    label="Filed"
                    value={fmtDateTime(expense.created_at)}
                  />
                  <Row
                    label="Currency"
                    value={expense.currency || "INR"}
                  />
                </dl>
                {expense.description && (
                  <div className="mt-4 rounded-md border border-input bg-muted/30 p-3">
                    <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" />
                      Description
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                      {expense.description}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Status &amp; decision
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <dl className="grid gap-3 sm:grid-cols-2">
                  <Row
                    label="Current status"
                    value={<StatusBadge status={expense.status} />}
                  />
                  {expense.decided_at ? (
                    <>
                      <Row
                        label="Decided"
                        value={fmtDateTime(expense.decided_at)}
                      />
                      <Row
                        label="Decided by"
                        value={
                          expense.approver_id != null
                            ? dir.label(
                                expense.approver_id,
                                expense.approver_name,
                              )
                            : "—"
                        }
                      />
                      <Row
                        label="Decision note"
                        value={expense.decision_notes || "—"}
                      />
                    </>
                  ) : (
                    <div className="rounded-md border border-dashed border-input px-3 py-2 text-sm text-muted-foreground sm:col-span-2">
                      {expense.status === "draft"
                        ? "This claim is a draft — submit it to send it for approval."
                        : expense.status === "submitted"
                          ? "Awaiting a decision from the approver."
                          : "No decision recorded."}
                    </div>
                  )}
                  {expense.status === "reimbursed" && (
                    <Row
                      label="Reimbursed via"
                      value={
                        expense.reimbursed_batch_id != null
                          ? `Batch #${expense.reimbursed_batch_id}`
                          : "—"
                      }
                    />
                  )}
                </dl>

                <div className="pt-1">
                  <ExpenseActions expense={expense} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Receipts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {category?.requires_receipt &&
                  expense.status === "draft" &&
                  (isOwner || isExpenseAdmin) && (
                    <Alert>
                      <AlertTitle>A receipt is required</AlertTitle>
                      <AlertDescription>
                        {categoryLabel} requires at least one receipt.
                        Attach one below, then use “Submit” above.
                      </AlertDescription>
                    </Alert>
                  )}
                <ReceiptManager
                  expenseId={expense.id}
                  status={expense.status}
                  canModify={canModify}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </ExpenseFeatureGate>
    </ExpensesModuleShell>
  );
}
